import { Controller } from "@hotwired/stimulus"

// Shared promise so the script is loaded only once across all instances
let googleMapsPromise = null

function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve()
  if (googleMapsPromise) return googleMapsPromise

  const apiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content
  if (!apiKey) return Promise.reject("No Google Maps API key")

  googleMapsPromise = new Promise((resolve, reject) => {
    window._initGooglePlaces = () => {
      delete window._initGooglePlaces
      resolve()
    }
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=_initGooglePlaces`
    script.async = true
    script.onerror = () => { googleMapsPromise = null; reject("Failed to load Google Maps") }
    document.head.appendChild(script)
  })

  return googleMapsPromise
}

/**
 * Reusable Google Places Autocomplete controller.
 *
 * Attach to any container that holds address form fields.
 * The Address input doubles as the autocomplete search field — users can
 * either type manually or pick a Google suggestion to auto-fill all fields.
 *
 * Targets: address, city, state, country, zipcode
 *
 * When a place is selected the controller:
 *   1. Fills address (street only), city, zipcode
 *   2. Sets the country <select> and dispatches "change" so the parent
 *      controller loads subregions
 *   3. Watches the state <select> for new <option>s, then sets the state
 */
export default class extends Controller {
  static targets = ["address", "city", "state", "country", "zipcode"]

  async connect() {
    try {
      await loadGoogleMaps()
      this._initAutocomplete()
    } catch (e) {
      console.warn("Google Places:", e)
    }
  }

  disconnect() {
    if (this._autocomplete) {
      google.maps.event.clearInstanceListeners(this._autocomplete)
      this._autocomplete = null
    }
    this._observer?.disconnect()
  }

  // ── Public actions ────────────────────────────────────────────────

  /** Call from data-action="change->google-places#countryChanged" on the country select */
  countryChanged() {
    this._applyCountryRestriction()
  }

  // ── Private ───────────────────────────────────────────────────────

  _initAutocomplete() {
    if (!this.hasAddressTarget) return

    this._autocomplete = new google.maps.places.Autocomplete(this.addressTarget, {
      fields: ["address_components", "formatted_address", "name"]
    })

    this._autocomplete.addListener("place_changed", () => this._onPlaceChanged())
    this._applyCountryRestriction()

    // Suppress Chrome's built-in address autofill.
    // Google Places sets autocomplete="off", but Chrome ignores that.
    // We force a Chrome-proof value and re-apply on focus because
    // the Places widget resets the attribute each time.
    this._suppressBrowserAutofill()
    this.addressTarget.addEventListener("focus", () => this._suppressBrowserAutofill())

    // Prevent form submit when pressing Enter on an open suggestions list
    this.addressTarget.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        const pac = document.querySelector(".pac-container:not([style*='display: none'])")
        if (pac) e.preventDefault()
      }
    })
  }

  _onPlaceChanged() {
    const place = this._autocomplete.getPlace()
    if (!place?.address_components) return

    let route = null
    let streetNumber = null
    let city = null
    let state = null
    let countryCode = null
    let zipcode = null

    for (const comp of place.address_components) {
      const type = comp.types[0]
      if (type === "route") route = comp.long_name
      else if (type === "street_number") streetNumber = comp.long_name
      else if ((type === "locality" || type === "administrative_area_level_2" || type === "sublocality_level_1") && !city) city = comp.long_name
      else if (type === "administrative_area_level_1") state = comp.long_name
      else if (type === "country") countryCode = comp.short_name.toLowerCase()
      else if (type === "postal_code") zipcode = comp.short_name
    }

    // Build a clean street-only address line
    let addressLine = null
    if (route) {
      addressLine = streetNumber ? `${route}, ${streetNumber}` : route
    } else if (place.formatted_address) {
      addressLine = place.formatted_address.split(",")[0]
    } else if (place.name && !/^[A-Za-z0-9\s-]{3,10}$/.test(place.name)) {
      addressLine = place.name
    }

    if (addressLine && this.hasAddressTarget) this.addressTarget.value = addressLine
    if (city && this.hasCityTarget) this.cityTarget.value = city
    if (zipcode && this.hasZipcodeTarget) this.zipcodeTarget.value = zipcode

    // Set country and trigger change so the parent controller loads subregions
    if (countryCode && this.hasCountryTarget) {
      const opt = Array.from(this.countryTarget.options).find(o => o.value === countryCode)
      if (opt) {
        this.countryTarget.value = countryCode
        this.countryTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    // After subregions load, set the state
    if (state && this.hasStateTarget) {
      this._pendingState = state
      this._watchStateOptions()
    }
  }

  _watchStateOptions() {
    this._observer?.disconnect()
    if (this._trySetState()) return

    this._observer = new MutationObserver(() => {
      if (this._trySetState()) this._observer.disconnect()
    })
    this._observer.observe(this.stateTarget, { childList: true })

    // Safety: stop watching after 5 s
    setTimeout(() => this._observer?.disconnect(), 5000)
  }

  _trySetState() {
    if (!this._pendingState) return true
    const opt = Array.from(this.stateTarget.options).find(
      o => o.value === this._pendingState || o.textContent === this._pendingState
    )
    if (opt) {
      this.stateTarget.value = opt.value
      this._pendingState = null
      return true
    }
    return false
  }

  _applyCountryRestriction() {
    if (!this._autocomplete || !this.hasCountryTarget) return
    const code = this.countryTarget.value
    if (code) {
      this._autocomplete.setComponentRestrictions({ country: code.toUpperCase() })
    } else {
      this._autocomplete.setComponentRestrictions({ country: [] })
    }
  }

  _suppressBrowserAutofill() {
    // "chrome-off" is a well-known hack — Chrome does not recognise it,
    // so it won't show the built-in address/contact autofill popup.
    this.addressTarget.setAttribute("autocomplete", "chrome-off")
  }
}
